import React from "react";
import "./scorebox.css";
import "./MultipleChoiceQuestion.css";
import InfoTooltip from "../../../InfoTooltip";
import StudentInfoTooltip from "../../../StudentInfoTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faLeftRight } from "@fortawesome/free-solid-svg-icons";
import TipButton from "../../../TipButton";
import { handleScoreChange } from "./helpers/scoreHelpers";
import CategoryTags from "./CategoryTags";

const MultipleCheckboxQuestion = ({
  question,
  options,
  correctAnswers,
  score,
  onChange,
  border,
  isStudentView,
  studentResponse,
  tip,
  questionCategories = [],
  questionId,
  showCategories = true,
  onFlip,
}) => {
  const onScoreChange = handleScoreChange(onChange);

  const handleStudentResponse = (index) => {
    if (isStudentView) {
      const newResponse =
        studentResponse && Array.isArray(studentResponse.answer)
          ? [...studentResponse.answer]
          : [];
      if (newResponse.includes(index)) {
        newResponse.splice(newResponse.indexOf(index), 1);
      } else {
        newResponse.push(index);
      }
      onChange({
        type: "MultipleCheckbox",
        answer: newResponse,
      });
    } else {
      handleCorrectAnswerChange(index);
    }
  };

  const handleQuestionChange = (e) => {
    onChange({ question: e.target.value });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ options: newOptions });
  };

  const handleCorrectAnswerChange = (index) => {
    const newCorrectAnswers = correctAnswers.includes(index)
      ? correctAnswers.filter((i) => i !== index)
      : [...correctAnswers, index];
    onChange({ correctAnswers: newCorrectAnswers });
  };

  const handleAddOption = () => {
    onChange({ options: [...options, ""] });
  };

  const handleRemoveOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    const newCorrectAnswers = correctAnswers
      .filter((i) => i !== index)
      .map((i) => (i > index ? i - 1 : i));
    onChange({ options: newOptions, correctAnswers: newCorrectAnswers });
  };

  const calculatePointValue = () => {
    if (correctAnswers.length === 0) return 0;
    return score / correctAnswers.length;
  };

  const pointValue = calculatePointValue();

  return (
    <div
      className={`question-container ${border} ${
        isStudentView ? "student-view" : ""
      }`}
    >
      <div className="question-header">
        {isStudentView ? (
          <>
            <div className="audio-recorder-question">{question}</div>
            <StudentInfoTooltip type="Multiple Checkbox" />
          </>
        ) : (
          <>
            <input
              type="text"
              value={question}
              onChange={handleQuestionChange}
              placeholder="Enter your question"
              className="question-input"
            />
            <InfoTooltip type="Multiple Checkbox" />
          </>
        )}
      </div>
      {options.map((option, index) => (
        <div key={index} className="option-container">
          <input
            type="checkbox"
            checked={
              isStudentView
                ? studentResponse && Array.isArray(studentResponse.answer)
                  ? studentResponse.answer.includes(index)
                  : false
                : correctAnswers.includes(index)
            }
            onChange={() => handleStudentResponse(index)}
            disabled={isStudentView && !onChange}
            className={correctAnswers.includes(index) ? "correct-answer" : ""}
          />
          <input
            type="text"
            value={option}
            onChange={(e) => handleOptionChange(index, e.target.value)}
            placeholder={`Option ${String.fromCharCode(65 + index)}`}
            readOnly={isStudentView}
            className={`${isStudentView ? "student-view" : ""} ${
              correctAnswers.includes(index) ? "correct-answer" : ""
            }`}
          />
          {!isStudentView && correctAnswers.includes(index) && (
            <span
              className="point-indicator"
              data-points={`+${pointValue.toFixed(2)} p`}
            >
              {window.matchMedia("(orientation: portrait)").matches
                ? `+${pointValue.toFixed(2)} p`
                : `+${pointValue.toFixed(2)} point!`}
            </span>
          )}
          {!isStudentView && (
            <button
              onClick={() => handleRemoveOption(index)}
              className="remove-option-button"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      ))}
      <div className="category-tags-container">
        <CategoryTags
          categories={questionCategories}
          onChange={(newCategories) =>
            onChange({
              questionCategories: newCategories,
            })
          }
          isStudentView={isStudentView}
          showCategories={showCategories}
        />
      </div>
      <div className="question-footer">
        <div className="question-footer-flex">
          <div
            className={
              isStudentView ? "student-button-wrapper" : "button-wrapper"
            }
          >
            {!isStudentView && (
              <button
                onClick={handleAddOption}
                className="question-footer-button"
              >
                Add Option
              </button>
            )}
          </div>
          <TipButton
            tip={tip}
            onChange={onChange}
            isStudentView={isStudentView}
            onFlip={onFlip}
          />
          <div className="scorebox-container">
            <label htmlFor="score">Score:</label>
            <input
              type="number"
              id="score"
              value={score}
              readOnly={isStudentView}
              onChange={onScoreChange}
              min="0"
              step="1"
              className={isStudentView ? "student-view" : ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultipleCheckboxQuestion;
